import { TFunction } from 'i18next';

import { InstantReflex, ReflexStep } from 'shiftNotes/_types';

import { GUIDANCE_STEP_TYPES } from '../NotesStack/NoteCardReflexStepContent';

import { Phase, StepFeedback } from './types';

export function sortSteps(steps: ReflexStep[] | undefined): {
  answerSteps: ReflexStep[];
  guidanceSteps: ReflexStep[];
} {
  const sorted = [...(steps ?? [])].sort((a, b) => a.order - b.order);
  return {
    answerSteps: sorted.filter((step) => step.step_type === 'give_answer'),
    guidanceSteps: sorted.filter((step) =>
      GUIDANCE_STEP_TYPES.has(step.step_type)
    ),
  };
}

export function computeInitialFeedbackState(instantReflex: InstantReflex): {
  currentStep: number;
  feedback: Record<string, StepFeedback>;
  overallComment: string;
  phase: Phase;
} {
  const { answerSteps, guidanceSteps } = sortSteps(instantReflex.steps);
  const ratedSteps = [...answerSteps, ...guidanceSteps];
  const alreadyReviewed = ratedSteps.some(
    (step) => step.feedback_status != null
  );
  if (!alreadyReviewed) {
    return {
      currentStep: 1,
      feedback: {},
      overallComment: '',
      phase: 'editing',
    };
  }
  return {
    currentStep: ratedSteps.length + 1,
    feedback: Object.fromEntries(
      ratedSteps
        .filter((step) => step.feedback_status != null)
        .map((step) => [
          step.id,
          {
            rating:
              step.feedback_status === 'disliked' ? 'negative' : 'positive',
            comment: step.feedback ?? '',
          } satisfies StepFeedback,
        ])
    ),
    overallComment: instantReflex.feedback ?? '',
    phase: 'thanks',
  };
}

interface SectionLabelArgs {
  isLoadingVersion: boolean;
  isSubmitStep: boolean;
  isEmptyReflex: boolean;
  activeStep: ReflexStep | null | undefined;
  answerSteps: ReflexStep[];
  guidanceSteps: ReflexStep[];
  t: TFunction;
}

export function buildSectionLabel({
  isLoadingVersion,
  isSubmitStep,
  isEmptyReflex,
  activeStep,
  answerSteps,
  guidanceSteps,
  t,
}: SectionLabelArgs): string {
  if (isLoadingVersion) return t('noteCardGuidance.title');
  if (isSubmitStep && isEmptyReflex)
    return t('reflexFeedback.emptyReflexTitle');
  if (isSubmitStep) return t('reflexFeedback.submitStepTitle');
  if (activeStep && activeStep.step_type === 'give_answer') {
    const answerIndex = answerSteps.indexOf(activeStep) + 1;
    return answerSteps.length > 1
      ? `${t('noteCardAnswers.title')} ${answerIndex}/${answerSteps.length}`
      : t('noteCardAnswers.title');
  }
  const guidanceIndex = activeStep ? guidanceSteps.indexOf(activeStep) + 1 : 1;
  return `${t('noteCardGuidance.title')} ${guidanceIndex}/${
    guidanceSteps.length
  }`;
}
